import React, { useEffect, useState, useContext } from "react";
import FileInput, { loadFile } from "common/FileInput";
import ActionsContext from "common/ActionsContext";
import Modal from "common/Modal.jsx";
import { capitalize } from "common/utils";

const importMethods = ["merge", "replace"];

export const importActionTriggers = ({
  importable,
  cliName,
  label,
  labelSingle,
  methods = importMethods,
}) =>
  importable !== true
    ? []
    : methods.map((method) => ({
        label: `${capitalize(method)} ${label || labelSingle} from file`,
        signal: `show-import-${cliName}`,
        method, // sent as param with signal
      }));

const doesNothing = () => {};

const intoItemOfFields = (fields) => (row) => {
  const values = fields.length > 1 ? row.split(/\s+/) : [row.trim("\r")];
  return Object.fromEntries(
    fields.map(({ name }, index) => [name, values[index]])
  );
};
const parseIntoItemsOfFields =
  (fields, parseLine = undefined) =>
  (input) => {
    parseLine = parseLine || intoItemOfFields(fields);
    return input.trim("\n").split("\n").map(parseLine);
  };

const ReportFailures = ({ failed = [] }) =>
  failed.length === 0 ? null : (
    <div className="padding-t-10 align-left color-red">
      <p>Some errors found:</p>
      <ul>
        {failed.map(({ line, error }) => (
          <li key={line} className="align-left">
            Error at line {line + 1}: {String(error)}.
          </li>
        ))}
      </ul>
    </div>
  );

const alreadyPresentOut = ({ error }) =>
  String(error).match(/already present\.?$/) === null;

const ReportResult = ({ allowed = [], failed = [], label = "Items" }) => (
  <div className="padding-t-20">
    <span>
      Loaded {allowed.length} {label}.
    </span>
    <ReportFailures failed={failed.filter(alreadyPresentOut)} />
  </div>
);

const SourceFileInput = ({ name, onLoad = doesNothing }) => {
  const handleSourceChange = (event) => {
    const [source] = event.target.files;
    onLoad(loadFile(source));
  };
  return (
    <FileInput
      text="Browse..."
      label={null}
      name={name}
      onChange={handleSourceChange}
    />
  );
};

export const SpecificImportModal = ({
  signal = '',
  label = undefined,
  labelSingle = "",
  fields = [],
  importLineParse = undefined,
  doBatch = doesNothing,
  method: methodParam = undefined,
}) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(methodParam);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const clearReport = () => {
    setError(null);
    setResult(null);
  };
  const actions = useContext(ActionsContext);
  const doOpen = () => {
    setOpen(true);
    clearReport();
  };
  const doClose = () => {
    setOpen(false);
  };
  useEffect(
    () =>
      actions.recv(signal, function ({ method }) {
        if (method !== undefined) {
          setMethod(method);
        }
        doOpen();
      }),
    [signal]
  );
  if (open !== true) {
    return null;
  }

  const handleLoad = (load) =>
    load
      .then(parseIntoItemsOfFields(fields, importLineParse))
      .then((list) => doBatch(list, method))
      .then(setResult)
      .catch((error) => setError(String(error)));
  return (
    <Modal
      title={`${
        method === "replace" ? "Replace" : "Merge"
      } ${labelSingle} from .txt file:`}
      superIcon={method ? "add_circle" : "add_circle"}
      onClose={doClose}
      closeLabel={result === null ? "CLOSE" : "OK"}
      content={() => (
        <>
          <SourceFileInput name="source" onLoad={handleLoad} />
          {result === null ? null : (
            <ReportResult label={label || labelSingle} {...result} />
          )}
        </>
      )}
      footerMessage={
        (error !== null && (
          <span className="modal-err-msg color-red align-left">{error}</span>
        )) ||
        null
      }
    />
  );
};

const ImportModal = ({cliName, ...settings}) =>
  <SpecificImportModal
    signal={`show-import-${cliName}`}
    {...settings}
  />


export default ImportModal;
